import { useCallback, useEffect, useRef, useState } from 'react';

import {
  buildPhotoCaptureLogEntry,
  buildSpeechLogEntry,
  buildSpeechUnsupportedLogEntry,
  buildVoiceAppliedLogEntry,
  buildVoiceProposedLogEntry,
  buildVoiceRejectedLogEntry,
  sortActivityLogEntries,
  type VideoToolboxActivityLogEntry,
} from './videoToolboxActivityLogLib';

/** Session-only activity log for the toolbox scroll section (cleared when the flyout closes). */
export const useVideoToolboxActivityLog = (open: boolean) => {
  const [entries, setEntries] = useState<VideoToolboxActivityLogEntry[]>([]);
  const speechEntrySequenceRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setEntries([]);
      speechEntrySequenceRef.current = 0;
    }
  }, [open]);

  const createSpeechEntryId = useCallback(() => {
    speechEntrySequenceRef.current += 1;
    return `speech-${speechEntrySequenceRef.current}`;
  }, []);

  const recordUserMessage = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setEntries(current =>
      sortActivityLogEntries([
        buildSpeechLogEntry(trimmed, createSpeechEntryId(), Date.now()),
        ...current,
      ])
    );
  }, [createSpeechEntryId]);

  const recordPhotoCapture = useCallback((photoId: string, scopeDisplayName: string) => {
    setEntries(current =>
      sortActivityLogEntries([
        buildPhotoCaptureLogEntry(photoId, scopeDisplayName),
        ...current,
      ])
    );
  }, []);

  const recordSpeechUnsupported = useCallback(() => {
    setEntries(current => {
      if (current.some(entry => entry.kind === 'speech-unsupported')) return current;
      return sortActivityLogEntries([buildSpeechUnsupportedLogEntry(), ...current]);
    });
  }, []);

  const recordVoiceApplied = useCallback((
    summary: string,
    utterance: string,
    confidence: number,
    jevDetail: string,
  ) => {
    setEntries(current =>
      sortActivityLogEntries([
        buildVoiceAppliedLogEntry(summary, utterance, confidence, jevDetail, `voice-applied-${crypto.randomUUID()}`),
        ...current.filter(entry => entry.kind !== 'voice-proposed'),
      ])
    );
  }, []);

  const recordVoiceProposed = useCallback((
    proposalId: string,
    summary: string,
    utterance: string,
    confidence: number,
    jevDetail: string,
  ) => {
    setEntries(current =>
      sortActivityLogEntries([
        buildVoiceProposedLogEntry(proposalId, summary, utterance, confidence, jevDetail),
        ...current.filter(entry => entry.kind !== 'voice-proposed'),
      ])
    );
  }, []);

  const recordVoiceRejected = useCallback((utterance: string, reason: string) => {
    setEntries(current =>
      sortActivityLogEntries([
        buildVoiceRejectedLogEntry(utterance, reason, `voice-rejected-${crypto.randomUUID()}`),
        ...current.filter(entry => entry.kind !== 'voice-proposed'),
      ])
    );
  }, []);

  return {
    entries,
    recordUserMessage,
    recordPhotoCapture,
    recordSpeechUnsupported,
    recordVoiceApplied,
    recordVoiceProposed,
    recordVoiceRejected,
  };
};
